import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Layers, Sparkles } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import { BoqBomFixedToolbar } from '../../components/boq-bom/BoqBomScreenHeader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { useSmartBack } from '../../../shared/navigation';
import { resolveProjectIdFromLocation } from '../../components/ai/AiUiShared';
import { TASK_MANAGEMENT_PATH } from '../../components/ai/opsAiNavigation';
import FormInput from '../../../shared/components/ui/FormInput';
import Loader from '../../../shared/components/ui/Loader';
import {
  getAllBlocks,
  getBoqBomPresence,
  getWorkTypesByType
} from '../../services/api/implementation/impl';
import {
  mapPresenceAccommodationToCard,
  mapPresenceGroupBlockToCard,
  mapProjectCommonFromPresence
} from '../../utils/boqBomExtract';
import {
  filterProjectCommonWorkTypes,
  getBoqBomGroupBlockPath,
  getBoqBomUploadPath,
  getBoqBomFlowApiOptions,
  resolveBoqBomFlowApiOptions
} from '../../utils/boqBomRoutes';

const BOQ_BOM_PROJECT_STORAGE_KEY = 'boqBomSelectedProjectId';
const PUBLIC_URL = process.env.PUBLIC_URL || '';
const GROUP_ACC_ICON = `${PUBLIC_URL}/group_acc.png`;
const INDEPENDENT_ACC_SECTION_ICON = `${PUBLIC_URL}/independent_acc_1.png`;
const INDEPENDENT_ACC_CARD_ICON = `${PUBLIC_URL}/independent_acc.png`;
const COMMON_AREA_SECTION_ICON = `${PUBLIC_URL}/common_area_1.png`;
const COMMON_AREA_CARD_ICON = `${PUBLIC_URL}/common_area_2.png`;

const SECTION_ICON_CLASS = 'h-6 w-6 shrink-0 object-contain';

const CardIconBadge = ({ src }) => (
  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#E8F4F6]">
    <img src={src} alt="" className="h-6 w-6 object-contain" aria-hidden />
  </span>
);

const tabs = [
  { key: 'boq', label: 'BOQ' },
  { key: 'bom', label: 'BOM' }
];

const SectionHeader = ({ title, iconSrc, icon: Icon, isOpen, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={isOpen}
    className="flex w-full items-center justify-between rounded-xl bg-[#E5F0F2] border border-[#D2E3E6] px-4 py-3 text-left hover:bg-[#DAE8EB] transition-colors">
    <div className="flex items-center gap-2">
      {iconSrc ? (
        <img src={iconSrc} alt="" className={SECTION_ICON_CLASS} aria-hidden />
      ) : Icon ? (
        <Icon className="h-6 w-6 shrink-0 text-[#2A565E]" aria-hidden />
      ) : (
        <span className="text-[#2A565E] text-sm">◈</span>
      )}
      <h3 className="text-[18px] font-medium text-[#1E2328] leading-none tracking-tight">
        {title}
      </h3>
    </div>
    <ChevronDown
      size={20}
      className={`shrink-0 text-gray-600 transition-transform duration-200 ${
        isOpen ? 'rotate-180' : ''
      }`}
      aria-hidden
    />
  </button>
);

const AccordionSection = ({ title, iconSrc, icon, isOpen, onToggle, children }) => (
  <div className="rounded-xl border border-[#D2E3E6] bg-white overflow-hidden">
    <SectionHeader title={title} iconSrc={iconSrc} icon={icon} isOpen={isOpen} onToggle={onToggle} />
    {isOpen ? <div className="px-4 pb-4 pt-3">{children}</div> : null}
  </div>
);

const BoqBomPill = ({ activeTab, showBoq = true, showBom = true, className = 'mt-3' }) => {
  if (!showBoq && !showBom) return null;

  return (
    <div className={`${className} flex items-center gap-3`} aria-hidden>
      {showBoq ? (
        <span
          className={`inline-flex h-8 min-w-[74px] items-center justify-center rounded-md border text-[11px] font-semibold ${
            activeTab === 'boq'
              ? 'bg-[#2A6A73] text-white border-[#2A6A73]'
              : 'bg-white text-[#6B7A80] border-[#7FA3AA]'
          }`}>
          BOQ
        </span>
      ) : null}
      {showBom ? (
        <span
          className={`inline-flex h-8 min-w-[74px] items-center justify-center rounded-md border text-[11px] font-semibold ${
            activeTab === 'bom'
              ? 'bg-[#2A6A73] text-white border-[#2A6A73]'
              : 'bg-white text-[#6B7A80] border-[#7FA3AA]'
          }`}>
          BOM
        </span>
      ) : null}
    </div>
  );
};

const BlockCard = ({ title, area, levels, activeTab, hasBoq = false, hasBom = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-lg border border-[#9AB7BC] bg-white p-3 text-left w-full hover:border-[#2A6A73] transition-colors">
    <div className="flex items-center gap-2.5">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#E8F4F6]">
        <Layers className="h-5 w-5 text-[#2A6A73]" aria-hidden />
      </span>
      <h4 className="text-[16px] font-medium text-[#202428] leading-none">{title}</h4>
    </div>
    <div className="mt-3 space-y-1 text-sm text-[#6C7278]">
      <div className="flex justify-between">
        <span>Block Area:</span>
        <span>{area}</span>
      </div>
      <div className="flex justify-between">
        <span>Levels:</span>
        <span>{levels}</span>
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        {hasBoq ? (
          <span className="text-[10px] text-[#7A8A8F] border border-gray-300 rounded-md px-2 py-1">
            BOQ Created
          </span>
        ) : null}
        {hasBom ? (
          <span className="text-[10px] text-[#2A6A73] border border-[#9AB7BC] rounded-md px-2 py-1">
            BOM Created
          </span>
        ) : null}
      </div>
      <BoqBomPill activeTab={activeTab} showBoq={!hasBoq} showBom={!hasBom} className="" />
    </div>
  </button>
);

const COMMON_AREA_CARD_TITLE = 'Common Area';

const CommonAreaCard = ({
  items,
  activeTab,
  iconSrc,
  hasBoq = false,
  hasBom = false,
  onClick
}) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-lg border border-[#7DA0A7] bg-white p-3 text-left w-full hover:border-[#2A6A73] transition-colors">
    <div className="flex items-center gap-2.5 text-[15px] font-medium text-[#263238]">
      {iconSrc ? <CardIconBadge src={iconSrc} /> : <span className="text-[#6F8A90]">▦</span>}
      <span>{COMMON_AREA_CARD_TITLE}</span>
    </div>
    <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-[#6C7278]">
      {items.map((item) => (
        <li key={item.id ?? item.name} className="flex items-center gap-2 min-w-0">
          <span className="h-1 w-1 shrink-0 rounded-full bg-[#9AB7BC]" aria-hidden />
          <span className="truncate">{item.name}</span>
        </li>
      ))}
    </ul>
    <div className="mt-3 flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        {hasBoq ? (
          <span className="text-[10px] text-[#7A8A8F] border border-gray-300 rounded-md px-2 py-1">
            BOQ Created
          </span>
        ) : null}
        {hasBom ? (
          <span className="text-[10px] text-[#2A6A73] border border-[#9AB7BC] rounded-md px-2 py-1">
            BOM Created
          </span>
        ) : null}
      </div>
      <BoqBomPill
        activeTab={activeTab}
        showBoq={!hasBoq}
        showBom={!hasBom}
        className=""
      />
    </div>
  </button>
);

const AccommodationCard = ({
  title,
  area,
  buildUp,
  carpet,
  activeTab,
  iconSrc,
  hasBoq = false,
  hasBom = false,
  onClick,
  onGenerateBom
}) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-lg border border-[#9AB7BC] bg-white p-3 text-left w-full hover:border-[#2A6A73] transition-colors">
    <div className="flex items-center gap-2.5">
      {iconSrc ? <CardIconBadge src={iconSrc} /> : <span className="text-[#6F8A90] text-sm">⌂</span>}
      <h4 className="text-[16px] font-medium text-[#202428] leading-none">{title}</h4>
    </div>
    <div className="mt-3 space-y-1 text-sm text-[#6C7278]">
      <div className="flex justify-between">
        <span>Plot Area:</span>
        <span>{area}</span>
      </div>
      <div className="flex justify-between">
        <span>Build Up:</span>
        <span>{buildUp}</span>
      </div>
      <div className="flex justify-between">
        <span>Carpet Area:</span>
        <span>{carpet}</span>
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        {hasBoq ? (
          <span className="text-[10px] text-[#7A8A8F] border border-gray-300 rounded-md px-2 py-1">
            BOQ Created
          </span>
        ) : null}
        {hasBom ? (
          <span className="text-[10px] text-[#2A6A73] border border-[#9AB7BC] rounded-md px-2 py-1">
            BOM Created
          </span>
        ) : null}
        {hasBoq && !hasBom ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onGenerateBom?.();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onGenerateBom?.();
              }
            }}
            className="rounded-md border border-gray-300 px-2 py-1 text-[11px] text-[#576067] hover:border-[#2A6A73] hover:text-[#2A6A73] transition-colors">
            Generate BOM
          </span>
        ) : null}
      </div>
      <BoqBomPill activeTab={activeTab} showBoq={!hasBoq} showBom={!hasBom} className="" />
    </div>
  </button>
);

const sectionIconByType = {
  GROUP: GROUP_ACC_ICON,
  INDEPENDENT: INDEPENDENT_ACC_SECTION_ICON
};

const cardIconByType = {
  GROUP: GROUP_ACC_ICON,
  INDEPENDENT: INDEPENDENT_ACC_CARD_ICON
};

const AccommodationSection = ({
  title,
  items,
  activeTab,
  isLoading,
  emptyMessage,
  accommodationType,
  isOpen,
  onToggle,
  onAccommodationClick,
  onGenerateBom
}) => (
  <AccordionSection
    title={title}
    iconSrc={sectionIconByType[accommodationType]}
    isOpen={isOpen}
    onToggle={onToggle}>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {isLoading ? (
        <div className="col-span-full py-8" aria-hidden />
      ) : items.length === 0 ? (
        <p className="col-span-full text-sm text-[#6B747A] py-4">{emptyMessage}</p>
      ) : (
        items.map((item) => (
          <AccommodationCard
            key={item.id ?? item.name}
            title={item.name}
            area={item.area}
            buildUp={item.buildUp}
            carpet={item.carpet}
            activeTab={activeTab}
            iconSrc={cardIconByType[accommodationType]}
            hasBoq={item.hasBoq}
            hasBom={item.hasBom}
            onClick={() => onAccommodationClick?.(item, accommodationType)}
            onGenerateBom={() => onGenerateBom?.(item, accommodationType)}
          />
        ))
      )}
    </div>
  </AccordionSection>
);

const BoqBomScreen = () => {
  const { collapsed } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { onBack } = useSmartBack({ fallback: TASK_MANAGEMENT_PATH });
  const requestedProjectId = resolveProjectIdFromLocation(location);
  const cameFromAi = new URLSearchParams(location.search || '').get('fromAi') === '1';
  const [activeTab] = useState('boq');
  const [openSections, setOpenSections] = useState({
    commonArea: true,
    group: true,
    groupBlock: true,
    independent: true
  });

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };
  const { projects, projectsForDropdown } = useProjectsList();
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    if (requestedProjectId) return requestedProjectId;
    const saved = localStorage.getItem(BOQ_BOM_PROJECT_STORAGE_KEY);
    return saved ? parseInt(saved, 10) : null;
  });

  const {
    register,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: { project: '' }
  });

  const projectFieldValue = watch('project');

  // Deep links (e.g. Create with AI → BOQ) carry ?project_id= and win over the last used project.
  useEffect(() => {
    if (!requestedProjectId || !projects?.length) return;
    if (!projects.some((p) => p.id === requestedProjectId)) return;
    setSelectedProjectId(requestedProjectId);
    setValue('project', requestedProjectId);
    localStorage.setItem(BOQ_BOM_PROJECT_STORAGE_KEY, String(requestedProjectId));
  }, [requestedProjectId, projects, setValue]);

  useEffect(() => {
    if (!projects?.length) return;

    if (selectedProjectId && projects.some((p) => p.id === selectedProjectId)) {
      setValue('project', selectedProjectId);
      return;
    }

    const firstProjectId = projects[0].id;
    setValue('project', firstProjectId);
    setSelectedProjectId(firstProjectId);
    localStorage.setItem(BOQ_BOM_PROJECT_STORAGE_KEY, String(firstProjectId));
  }, [projects, selectedProjectId, setValue]);

  useEffect(() => {
    if (!projectFieldValue) return;
    const projectId = parseInt(projectFieldValue, 10);
    if (!Number.isNaN(projectId) && projectId !== selectedProjectId) {
      setSelectedProjectId(projectId);
      localStorage.setItem(BOQ_BOM_PROJECT_STORAGE_KEY, String(projectId));
    }
  }, [projectFieldValue, selectedProjectId]);

  const selectedProject = useMemo(
    () => projects?.find((p) => p.id === selectedProjectId) ?? null,
    [projects, selectedProjectId]
  );

  const measurementUnit = selectedProject?.measurement_type || 'sq/ft';

  const { data: presenceData, isFetching: isPresenceFetching } = useQuery({
    queryKey: ['boqBomPresence', selectedProjectId],
    queryFn: () => getBoqBomPresence(selectedProjectId),
    enabled: !!selectedProjectId
  });

  const { data: allBlocks = [], isFetching: isBlocksFetching } = useQuery({
    queryKey: ['getAllBlocks', selectedProjectId],
    queryFn: () => getAllBlocks(selectedProjectId),
    enabled: !!selectedProjectId
  });

  const { data: commonWorkTypes = [], isFetching: isCommonWorkTypesFetching } = useQuery({
    queryKey: ['workTypes', 'Common'],
    queryFn: () => getWorkTypesByType('Common')
  });

  const blockMetaById = useMemo(() => {
    const map = new Map();
    (Array.isArray(allBlocks) ? allBlocks : []).forEach((block) => {
      if (block?.id != null) map.set(Number(block.id), block);
    });
    return map;
  }, [allBlocks]);

  const projectCommonAreaStatus = useMemo(
    () => mapProjectCommonFromPresence(presenceData?.projectCommon),
    [presenceData]
  );

  const commonAreaCards = useMemo(
    () => filterProjectCommonWorkTypes(commonWorkTypes),
    [commonWorkTypes]
  );

  const groupAccommodations = useMemo(
    () =>
      (Array.isArray(presenceData?.groupAccommodations) ? presenceData.groupAccommodations : []).map(
        (item) => mapPresenceAccommodationToCard(item, measurementUnit)
      ),
    [presenceData, measurementUnit]
  );

  const independentAccommodations = useMemo(
    () =>
      (Array.isArray(presenceData?.independent) ? presenceData.independent : [])
        .filter((item) => String(item?.accomodationType || '').toUpperCase() === 'INDEPENDENT')
        .map((item) => mapPresenceAccommodationToCard(item, measurementUnit)),
    [presenceData, measurementUnit]
  );

  const groupBlocks = useMemo(
    () =>
      (Array.isArray(presenceData?.groupBlocks) ? presenceData.groupBlocks : []).map((item) =>
        mapPresenceGroupBlockToCard(item, blockMetaById.get(Number(item?.blockId)), measurementUnit)
      ),
    [presenceData, blockMetaById, measurementUnit]
  );

  const isAccommodationsLoading = isPresenceFetching;

  const isPageLoading =
    isCommonWorkTypesFetching ||
    (!!selectedProjectId && (isPresenceFetching || isBlocksFetching));

  const navigateToAccommodation = (item, accommodationType, { returnTab } = {}) => {
    if (!item?.id) return;
    navigate(getBoqBomUploadPath(accommodationType, item.id), {
      state: {
        categoryName: item.name,
        accommodationId: item.id,
        accommodationType,
        projectId: selectedProjectId,
        projectName: selectedProject?.name,
        returnTab,
        hasBoq: item.hasBoq,
        hasBom: item.hasBom,
        boqId: item.boqId
      }
    });
  };

  const handleAccommodationClick = (item, accommodationType) => {
    navigateToAccommodation(item, accommodationType);
  };

  const handleGenerateBom = (item, accommodationType) => {
    navigateToAccommodation(item, accommodationType, { returnTab: 'BOM' });
  };

  const handleGroupBlockClick = (block) => {
    if (!block?.accommodationId) {
      toast.error('No GROUP accommodation linked to this block.');
      return;
    }
    navigate(getBoqBomGroupBlockPath(block.id), {
      state: {
        blockId: block.id,
        categoryName: block.name,
        accommodationId: block.accommodationId,
        projectId: selectedProjectId,
        projectName: selectedProject?.name,
        hasBoq: block.hasBoq,
        hasBom: block.hasBom,
        boqId: block.boqId,
        flowApiOptions: resolveBoqBomFlowApiOptions(
          `/builder/operations/boq-bom/group-block/${block.id}`
        )
      }
    });
  };

  return (
    <Layout>
      <Loader isVisible={isPageLoading} />
      <BoqBomFixedToolbar collapsed={collapsed} backArrow onBack={onBack} />

      <div className="px-6 pb-6 bg-[#F4F6F7] min-h-screen pt-[4.75rem]">
        <div className="grid grid-cols-1 gap-4">
          {cameFromAi ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
              <p className="flex items-center gap-2 text-sm text-indigo-900">
                <Sparkles className="h-4 w-4 shrink-0" />
                Opened from Create with AI
                {selectedProject?.name ? ` for ${selectedProject.name}` : ''} — add BOQ here, your
                template proposal is saved.
              </p>
              <button
                type="button"
                onClick={onBack}
                className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-100"
              >
                Back to AI proposal
              </button>
            </div>
          ) : null}
          <div>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-[26px] font-medium leading-tight tracking-tight text-[#1E2328]">
                  Accommodation
                </h1>
                <p className="text-[16px] leading-snug text-[#6B747A]">Manage Bill of Quantities</p>
              </div>
              <div className="boq-project-filter w-full max-w-[300px] shrink-0 sm:w-[300px]">
                <style>{`
                  .boq-project-filter .form-field-shell > div {
                    width: 100% !important;
                  }
                  .boq-project-filter .form-field-shell .relative > div {
                    min-width: 0;
                  }
                  .boq-project-filter .form-field-shell .relative > div > span:first-child {
                    display: block;
                    min-width: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  }
                `}</style>
                <div
                  className="form-field-shell rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 shadow-sm"
                  title={selectedProject?.name || 'Select project'}
                >
                  <FormInput
                    width={100}
                    label="Project"
                    register={register}
                    register_key="project"
                    isRequired={false}
                    type="dropdown"
                    options={projectsForDropdown}
                    validation={{ required: false }}
                    errors={errors}
                    hideAstrik={true}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <AccordionSection
                title={`Common Area (${commonAreaCards.length > 0 ? 1 : 0})`}
                iconSrc={COMMON_AREA_SECTION_ICON}
                isOpen={openSections.commonArea}
                onToggle={() => toggleSection('commonArea')}>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {isCommonWorkTypesFetching || isPresenceFetching ? (
                    <div className="col-span-full py-8" aria-hidden />
                  ) : commonAreaCards.length === 0 ? (
                    <p className="col-span-full text-sm text-[#6B747A] py-4">
                      No common area work types found.
                    </p>
                  ) : (
                    <CommonAreaCard
                      items={commonAreaCards}
                      activeTab={activeTab}
                      iconSrc={COMMON_AREA_CARD_ICON}
                      hasBoq={projectCommonAreaStatus.hasBoq}
                      hasBom={projectCommonAreaStatus.hasBom}
                      onClick={() =>
                        navigate(
                          `/builder/operations/boq-bom/common-area/${encodeURIComponent(COMMON_AREA_CARD_TITLE)}`,
                          {
                            state: {
                              categoryName: COMMON_AREA_CARD_TITLE,
                              workTypes: commonAreaCards,
                              projectId: selectedProjectId,
                              projectName: selectedProject?.name,
                              hasBoq: projectCommonAreaStatus.hasBoq,
                              hasBom: projectCommonAreaStatus.hasBom,
                              boqId: projectCommonAreaStatus.boqId,
                              flowApiOptions: getBoqBomFlowApiOptions(
                                `/builder/operations/boq-bom/common-area/${encodeURIComponent(COMMON_AREA_CARD_TITLE)}`
                              )
                            }
                          }
                        )
                      }
                    />
                  )}
                </div>
              </AccordionSection>

              <AccommodationSection
                title={`Group Accommodation (${groupAccommodations.length})`}
                items={groupAccommodations}
                activeTab={activeTab}
                isLoading={isAccommodationsLoading}
                accommodationType="GROUP"
                isOpen={openSections.group}
                onToggle={() => toggleSection('group')}
                onAccommodationClick={handleAccommodationClick}
                onGenerateBom={handleGenerateBom}
                emptyMessage={
                  selectedProjectId
                    ? 'No group accommodations found for this project.'
                    : 'Select a project to view group accommodations.'
                }
              />

              <AccordionSection
                title={`Group Block (${groupBlocks.length})`}
                icon={Layers}
                isOpen={openSections.groupBlock}
                onToggle={() => toggleSection('groupBlock')}>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {isPresenceFetching || isBlocksFetching ? (
                    <div className="col-span-full py-8" aria-hidden />
                  ) : groupBlocks.length === 0 ? (
                    <p className="col-span-full text-sm text-[#6B747A] py-4">
                      {selectedProjectId
                        ? 'No group blocks found for this project.'
                        : 'Select a project to view group blocks.'}
                    </p>
                  ) : (
                    groupBlocks.map((block) => (
                      <BlockCard
                        key={block.id}
                        title={block.name}
                        area={block.area}
                        levels={block.levels}
                        activeTab={activeTab}
                        hasBoq={block.hasBoq}
                        hasBom={block.hasBom}
                        onClick={() => handleGroupBlockClick(block)}
                      />
                    ))
                  )}
                </div>
              </AccordionSection>

              <AccommodationSection
                title={`Independent Accommodation (${independentAccommodations.length})`}
                items={independentAccommodations}
                activeTab={activeTab}
                isLoading={isAccommodationsLoading}
                accommodationType="INDEPENDENT"
                isOpen={openSections.independent}
                onToggle={() => toggleSection('independent')}
                onAccommodationClick={handleAccommodationClick}
                onGenerateBom={handleGenerateBom}
                emptyMessage={
                  selectedProjectId
                    ? 'No independent accommodations found for this project.'
                    : 'Select a project to view independent accommodations.'
                }
              />
            </div>
          </div>

          {/* Activity Logs — hidden until API is available
          <div className="xl:sticky xl:top-[5.5rem] h-fit">
            <div className="rounded-xl border border-gray-300 bg-white h-[calc(100vh-8rem)] min-h-[520px] flex flex-col">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-[24px] font-medium text-[#1F2529] leading-none">
                  Activity Logs
                </h3>
                <span className="text-gray-600">≡</span>
              </div>
              <div className="overflow-y-auto px-4 py-3 space-y-4">
                {activityLogs.map((log, idx) => (
                  <div key={`${log.user}-${idx}`} className="relative pl-8">
                    {idx !== activityLogs.length - 1 && (
                      <span className="absolute left-[11px] top-6 h-full border-l border-dashed border-gray-300" />
                    )}
                    <span className="absolute left-0 top-0 h-5 w-5 rounded-full bg-gray-100 border border-gray-300 flex items-center justify-center text-[10px] text-gray-500">
                      •
                    </span>
                    <p className="text-[13px] font-semibold text-[#1F2529]">{log.user}</p>
                    <p className="text-xs text-[#566068]">{log.action}</p>
                    <p className="text-xs text-[#8B949A]">◷ {log.time}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          */}
        </div>
      </div>
    </Layout>
  );
};

export default BoqBomScreen;
